import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { bind } from '../../../common/utils/decorators';
import { ErrorNotification, SuccessNotification } from '../../../common/utils/notifications/Notification';
import { FeatureFlagsType, getFeatureFlag } from '../../../config/launch-darkly/FeatureFlagsController';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { Tanium } from '../../../integrations/tanium/models/Tanium';
import { CheckTaniumIntegrationArgs, TaniumRepository, UpdateTaniumIntegrationArgs } from '../../../integrations/tanium/services/TaniumRepository';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { TaniumAccordion } from '../components/TaniumAccordion';

type State = BaseState & {
  isInitialLoading: boolean;
  isLoading: boolean;
  tanium: Tanium | null;
};

type Props = {};

export class TaniumController extends BaseController<Props, State> {
  private readonly integrationsRepository = new IntegrationsRepository();
  private readonly taniumRepository = new TaniumRepository();
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      isInitialLoading: true,
      isLoading: true,
      tanium: null,
    };
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.integrationsRepository.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const taniumEnabled = getFeatureFlag(FeatureFlagsType.ENABLE_TANIUM_INTEGRATION);
    if (!taniumEnabled) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }
    const permission = await this.permissionsService.canUpdateTanium();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    this.fetchData();
  }

  @bind
  async fetchData() {
    try {
      this.setState({ isLoading: true });
      const integrations = await this.integrationsRepository.getIntegrations();
      this.setState({ isInitialLoading: false, isLoading: false, tanium: integrations?.getTanium() ?? null });
    } catch (e: unknown) {
      this.setState({ isInitialLoading: false, isLoading: false, tanium: null });
      this.handleError(e);
    }
  }

  @bind
  async checkTaniumIntegration(args: CheckTaniumIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.taniumRepository.checkIntegrationConnection(args);
      if (testConnectionResult.isSuccessful()) {
        this.showMessage(new SuccessNotification(t('integrationList.tanium.testSuccess')));
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.tanium.testFail');
        this.showMessage(new ErrorNotification(message));
      }
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async triggerTaniumUpdate(args: UpdateTaniumIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      const taniumIntegration = await this.taniumRepository.updateIntegration(args);
      this.setState({ isLoading: false, tanium: taniumIntegration });
      this.showMessage(new SuccessNotification(t('integrationList.tanium.updateSuccess')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async triggerTaniumCheckAndUpdate(args: UpdateTaniumIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.taniumRepository.checkIntegrationConnection({
        accessToken: args.accessToken,
        url: args.url,
        trustedCaCertificate: args.trustedCaCertificate,
      });
      if (testConnectionResult.isSuccessful()) {
        await this.triggerTaniumUpdate(args);
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.tanium.testFail');
        this.showMessage(new ErrorNotification(message));
      }
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async handleTaniumUpdateClick(args: UpdateTaniumIntegrationArgs) {
    try {
      this.setState({ isLoading: true });
      if (args.url && args.accessToken) {
        await this.triggerTaniumCheckAndUpdate(args);
      } else {
        await this.triggerTaniumUpdate(args);
      }
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async unlinkTaniumIntegration() {
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('integrationList.tanium.title', null);
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.taniumRepository.unlinkIntegration();
      this.setState({ isLoading: false, tanium: null });
      this.showMessage(new SuccessNotification(t('integrationList.tanium.updateSuccess')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  render() {
    const { isInitialLoading, isLoading, tanium } = this.state;
    return this.renderBase(
      <SecondaryNav title={t(TabNameLocalizationKeys.TANIUM)} subtitle={t('integrationList.tanium.subtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        <BackButton sx={{ mb: 2 }} />
        <ComponentLoader loading={isLoading}>
          <TaniumAccordion
            canUpdate={true}
            isLoading={isInitialLoading}
            tanium={tanium}
            onConnectClick={this.handleTaniumUpdateClick}
            onTestClick={this.checkTaniumIntegration}
            onUnlinkClick={this.unlinkTaniumIntegration}
          />
        </ComponentLoader>
      </SecondaryNav>
    );
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getIntegrationBreadcrumbs({
      integrationName: t(TabNameLocalizationKeys.TANIUM),
      integrationPath: PATHS.INTEGRATION_TANIUM,
    });
  }
}
